// /workspaces — multi-tenancy admin: list workspaces the user belongs
// to, create new ones, switch active workspace, manage members + API
// keys per workspace.
//
// Three sections:
//   1. Workspace list + new workspace dialog + switch button per row
//   2. Selected workspace: members panel (invite by email, change role,
//      remove) — gated by owner|admin role
//   3. Selected workspace: API keys panel (mint, revoke). Plaintext
//      shown ONCE on creation; user copies and the value is gone.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getMe, listWorkspaces, createWorkspace, selectWorkspace,
  listMembers, inviteMember, removeMember,
  listApiKeys, createApiKey, revokeApiKey,
  countUnscopedSources, claimUnscopedSources,
} from '../api';
import type { WorkspaceWithRole, MemberRow } from '../api';
import type { UserPublic, ApiKeyRedacted, WorkspaceRole } from '@datahub/shared';
import { useToast } from '../components/Toast';
import { Table } from '../components/Table';
import TopHeader from '../components/TopHeader';

export default function WorkspacesPage(): React.ReactElement {
  const toast = useToast();
  const [me, setMe] = useState<UserPublic | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceWithRole[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState<WorkspaceWithRole | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [keys, setKeys] = useState<ApiKeyRedacted[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{ value: string; name: string; prefix: string } | null>(null);
  const [unscopedCount, setUnscopedCount] = useState<number>(0);
  const [claiming, setClaiming] = useState(false);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>('editor');
  const [keyName, setKeyName] = useState('');

  async function reload(): Promise<void> {
    setError(null);
    try {
      const [u, ws] = await Promise.all([getMe(), listWorkspaces(showAll)]);
      setMe(u);
      setWorkspaces(ws);
      if (!active && ws[0]) setActive(ws[0]);
    } catch (e) { setError(String(e)); }
  }

  useEffect(() => { reload(); }, [showAll]);

  useEffect(() => {
    if (!active) return;
    listMembers(active.workspace_id).then(setMembers).catch(e => setError(String(e)));
    listApiKeys(active.workspace_id).then(setKeys).catch(e => setError(String(e)));
    countUnscopedSources(active.workspace_id).then(setUnscopedCount).catch(() => setUnscopedCount(0));
  }, [active?.workspace_id]);

  async function onClaimUnscoped(): Promise<void> {
    if (!active) return;
    const ok = await toast.confirm({
      title: `Adopt ${unscopedCount} legacy source${unscopedCount === 1 ? '' : 's'} into ${active.name}?`,
      message: 'These sources were created before workspaces existed (or via un-authed flows like smoke tests). After claiming, they\'ll only be visible inside this workspace.',
      confirmLabel: 'Claim',
    });
    if (!ok) return;
    setClaiming(true);
    try {
      const r = await claimUnscopedSources(active.workspace_id);
      toast.show(`Claimed ${r.claimed} source${r.claimed === 1 ? '' : 's'}`);
      const next = await countUnscopedSources(active.workspace_id);
      setUnscopedCount(next);
    } catch (e) {
      toast.show(`Claim failed: ${String(e)}`);
    } finally {
      setClaiming(false);
    }
  }

  async function onCreate(): Promise<void> {
    if (!name.trim()) return;
    try {
      // Backend auto-selects the new workspace as active (sets dh_ws cookie).
      // Hard reload picks it up everywhere — sources list, switcher, etc.
      await createWorkspace(name.trim(), slug.trim() || undefined);
      setName(''); setSlug('');
      toast.show('Workspace created — set as active');
      window.location.reload();
    } catch (e) { toast.show(`Create failed: ${String(e)}`); }
  }

  async function onSwitch(ws: WorkspaceWithRole): Promise<void> {
    try {
      await selectWorkspace(ws.workspace_id);
      setActive(ws);
      toast.show(`Switched to ${ws.name}`);
    } catch (e) { toast.show(`Switch failed: ${String(e)}`); }
  }

  async function onInvite(): Promise<void> {
    if (!active || !inviteEmail.trim()) return;
    try {
      await inviteMember(active.workspace_id, inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      toast.show(`Invite sent to ${inviteEmail}`);
      const next = await listMembers(active.workspace_id);
      setMembers(next);
    } catch (e) { toast.show(`Invite failed: ${String(e)}`); }
  }

  async function onRemove(membershipId: string): Promise<void> {
    if (!active) return;
    const ok = await toast.confirm({ title: 'Remove member?', message: 'They lose access immediately.', confirmLabel: 'Remove', destructive: true });
    if (!ok) return;
    try {
      await removeMember(active.workspace_id, membershipId);
      const next = await listMembers(active.workspace_id);
      setMembers(next);
    } catch (e) { toast.show(`Remove failed: ${String(e)}`); }
  }

  async function onCreateKey(): Promise<void> {
    if (!active || !keyName.trim()) return;
    try {
      const k = await createApiKey(active.workspace_id, keyName.trim());
      setNewKey({ value: k.value, name: k.name, prefix: k.prefix });
      setKeyName('');
      const next = await listApiKeys(active.workspace_id);
      setKeys(next);
    } catch (e) { toast.show(`Create key failed: ${String(e)}`); }
  }

  async function onRevoke(id: string, prefix: string): Promise<void> {
    const ok = await toast.confirm({ title: `Revoke ${prefix}…?`, message: 'API calls using this key fail with 401 immediately.', confirmLabel: 'Revoke', destructive: true });
    if (!ok) return;
    try {
      await revokeApiKey(id);
      const next = active ? await listApiKeys(active.workspace_id) : [];
      setKeys(next);
    } catch (e) { toast.show(`Revoke failed: ${String(e)}`); }
  }

  const canManage = active && (active.role === 'owner' || active.role === 'admin' || me?.is_super_admin);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)' }}>
      <TopHeader env="Workspaces" hideLive />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 8px' }}>Workspaces</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 24px' }}>
          A workspace is a tenant boundary. Sources, triggers, and secrets live inside one.
          Members access them through their role: <code style={{ fontFamily: 'var(--mono)' }}>owner</code> &gt; <code style={{ fontFamily: 'var(--mono)' }}>admin</code> &gt; <code style={{ fontFamily: 'var(--mono)' }}>editor</code> &gt; <code style={{ fontFamily: 'var(--mono)' }}>viewer</code>.
          {me?.is_super_admin && ' As super-admin you see every workspace via the toggle below.'}
        </p>
        {error && <div style={{ padding: 12, background: 'oklch(0.95 0.05 30)', color: 'oklch(0.32 0.07 28)', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

        {/* New workspace */}
        <div style={{ padding: 16, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-elev)', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Create workspace</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Acme Inc"
              style={{ flex: '1 1 240px', padding: 8, border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--bg)' }} />
            <input
              value={slug} onChange={e => setSlug(e.target.value.toLowerCase())}
              placeholder="acme (optional slug)"
              style={{ flex: '1 1 180px', padding: 8, border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--mono)', background: 'var(--bg)' }} />
            <button onClick={onCreate} disabled={!name.trim()} style={{ padding: '8px 16px', background: 'var(--ink)', color: 'var(--bg)', border: 'none', borderRadius: 6, fontSize: 13, cursor: name.trim() ? 'pointer' : 'not-allowed', opacity: name.trim() ? 1 : 0.5 }}>
              Create
            </button>
          </div>
        </div>

        {/* List */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Your workspaces</div>
            {me?.is_super_admin && (
              <label style={{ fontSize: 12, color: 'var(--ink-2)', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
                Show every workspace (super-admin)
              </label>
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {workspaces.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No workspaces yet — create one above.</div>}
            {workspaces.map(ws => (
              <div key={ws.workspace_id} onClick={() => setActive(ws)} style={{
                padding: '10px 14px', border: '1px solid', borderColor: active?.workspace_id === ws.workspace_id ? 'var(--ink)' : 'var(--line)',
                borderRadius: 6, background: active?.workspace_id === ws.workspace_id ? 'var(--bg-elev)' : 'transparent',
                cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{ws.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
                    {ws.workspace_id}{ws.slug ? ` · /${ws.slug}` : ''}{ws.role ? ` · ${ws.role}` : ''}
                  </div>
                </div>
                {me && !me.is_super_admin && me.current_workspace_id !== ws.workspace_id && (
                  <button onClick={e => { e.stopPropagation(); onSwitch(ws); }}
                    style={{ padding: '4px 10px', fontSize: 11, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer' }}>
                    Switch
                  </button>
                )}
                {me?.current_workspace_id === ws.workspace_id && (
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>active</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Members */}
        {active && (
          <div style={{ marginBottom: 24, padding: 16, border: '1px solid var(--line)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Members of {active.name}</div>
            {canManage && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <input
                  value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="alice@partner.com"
                  type="email"
                  style={{ flex: '1 1 240px', padding: 8, border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--bg)' }} />
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value as WorkspaceRole)}
                  style={{ padding: 8, border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--bg)', color: 'var(--ink)' }}>
                  <option value="viewer">viewer</option>
                  <option value="editor">editor</option>
                  <option value="admin">admin</option>
                  <option value="owner">owner</option>
                </select>
                <button onClick={onInvite} disabled={!inviteEmail.trim()}
                  style={{ padding: '8px 16px', background: 'var(--ink)', color: 'var(--bg)', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Invite
                </button>
              </div>
            )}
            <Table<MemberRow>
              rows={members}
              getRowKey={m => m.membership_id}
              columns={[
                {
                  id: 'user', label: 'user',
                  render: m => (
                    <>
                      <div style={{ fontSize: 13 }}>{m.user?.name ?? m.invited_email ?? '(unknown)'}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{m.user?.email ?? m.invited_email}</div>
                    </>
                  ),
                },
                { id: 'role', label: 'role', mono: true, width: 90, render: m => m.role },
                {
                  id: 'status', label: 'status', mono: true, width: 130,
                  render: m => m.user_id
                    ? <span style={{ color: 'var(--ink-2)' }}>joined</span>
                    : <span style={{ color: 'oklch(0.55 0.18 70)' }}>invite pending</span>,
                },
                {
                  id: 'actions', label: '', align: 'right', width: 90, stopPropagation: true,
                  render: m => canManage ? (
                    <button onClick={() => onRemove(m.membership_id)}
                      style={{ padding: '3px 8px', fontSize: 11, background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}>
                      Remove
                    </button>
                  ) : null,
                },
              ]}
              emptyState={
                <div style={{ padding: 16, color: 'var(--ink-3)', fontSize: 12, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 6 }}>
                  No members yet — invite by email above.
                </div>
              }
            />
          </div>
        )}

        {/* Legacy un-scoped sources — adopt into the active workspace.
            Only shown when there's at least one un-scoped source AND the
            user can manage the workspace (owner / admin / super-admin). */}
        {active && canManage && unscopedCount > 0 && (
          <div style={{ marginBottom: 24, padding: 14, border: '1px solid oklch(0.78 0.10 75)', background: 'oklch(0.97 0.04 75)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, color: 'oklch(0.32 0.07 70)' }}>
              {unscopedCount} legacy source{unscopedCount === 1 ? '' : 's'} not in any workspace
            </div>
            <div style={{ fontSize: 12, color: 'oklch(0.42 0.06 70)', marginBottom: 10 }}>
              Pre-multi-tenancy or un-authed creates (smoke tests, MCP without a key) live outside the workspace boundary.
              Adopt them into <strong>{active.name}</strong> so they show up here only.
            </div>
            <button onClick={onClaimUnscoped} disabled={claiming}
              style={{ padding: '6px 14px', fontSize: 12, background: 'oklch(0.55 0.18 70)', color: 'white', border: 'none', borderRadius: 6, cursor: claiming ? 'wait' : 'pointer' }}>
              {claiming ? 'Claiming…' : `Claim ${unscopedCount} source${unscopedCount === 1 ? '' : 's'}`}
            </button>
          </div>
        )}

        {/* API keys */}
        {active && (
          <div style={{ padding: 16, border: '1px solid var(--line)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>API keys for {active.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 10 }}>
              Use as <code style={{ fontFamily: 'var(--mono)' }}>Authorization: Bearer dh_…</code> on REST and MCP requests.
              The full token is shown <strong>once</strong> — copy it now or revoke + mint a new one.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                value={keyName} onChange={e => setKeyName(e.target.value)}
                placeholder="Claude Desktop · MacBook"
                style={{ flex: '1 1 240px', padding: 8, border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--bg)' }} />
              <button onClick={onCreateKey} disabled={!keyName.trim()}
                style={{ padding: '8px 16px', background: 'var(--ink)', color: 'var(--bg)', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                New key
              </button>
            </div>
            {newKey && <NewKeyRevealCard newKey={newKey} onDismiss={() => setNewKey(null)} onCopy={() => toast.show('Copied')} />}
            <Table<ApiKeyRedacted>
              rows={keys}
              getRowKey={k => k.api_key_id}
              rowStyle={k => ({ opacity: k.revoked_at ? 0.55 : 1 })}
              columns={[
                {
                  id: 'name', label: 'name',
                  render: k => (
                    <>
                      <div style={{ fontSize: 13 }}>{k.name} {k.revoked_at && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>· revoked</span>}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{k.prefix}…</div>
                    </>
                  ),
                },
                { id: 'created', label: 'created', mono: true, width: 110, render: k => <span style={{ color: 'var(--ink-3)' }}>{new Date(k.created_at).toLocaleDateString()}</span> },
                { id: 'last_used', label: 'last used', mono: true, width: 110, render: k => <span style={{ color: 'var(--ink-3)' }}>{k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : '—'}</span> },
                {
                  id: 'actions', label: '', align: 'right', width: 90, stopPropagation: true,
                  render: k => k.revoked_at ? null : (
                    <button onClick={() => onRevoke(k.api_key_id, k.prefix)}
                      style={{ padding: '3px 8px', fontSize: 11, background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}>
                      Revoke
                    </button>
                  ),
                },
              ]}
              emptyState={
                <div style={{ padding: 16, color: 'var(--ink-3)', fontSize: 12, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 6 }}>
                  No keys yet — mint one above.
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Post-create card: shows the plaintext token + ready-to-copy snippets
// for curl, MCP (Claude Desktop), and the env-var form. The full key
// value is never re-fetchable, so this is the ONLY moment the operator
// can grab it. Three tabs because operators wire keys into different
// places: REST clients, Claude Desktop / Cursor, shell envs.
function NewKeyRevealCard({ newKey, onDismiss, onCopy }: {
  newKey: { value: string; name: string; prefix: string };
  onDismiss: () => void;
  onCopy: () => void;
}): React.ReactElement {
  const [tab, setTab] = useState<'curl' | 'mcp' | 'env'>('curl');
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://datahub.bornfight.com';

  const snippets: Record<typeof tab, string> = {
    curl: `curl ${base}/api/v1/sources \\\n  -H "Authorization: Bearer ${newKey.value}"`,
    mcp: JSON.stringify({
      mcpServers: {
        datahub: {
          command: 'npx',
          args: ['-y', '@datahub/mcp-server'],
          env: {
            DATAHUB_BASE_URL: base,
            DATAHUB_API_KEY: newKey.value,
          },
        },
      },
    }, null, 2),
    env: `export DATAHUB_BASE_URL="${base}"\nexport DATAHUB_API_KEY="${newKey.value}"`,
  };

  function copy(text: string): void {
    void navigator.clipboard.writeText(text);
    onCopy();
  }

  return (
    <div style={{ padding: 14, background: 'oklch(0.96 0.04 145)', border: '1px solid oklch(0.78 0.10 145)', borderRadius: 6, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: 'oklch(0.32 0.06 145)' }}>
        Save this key — it won't be shown again
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
        <code style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 13, padding: 8, background: 'var(--bg)', borderRadius: 4, wordBreak: 'break-all' }}>{newKey.value}</code>
        <button onClick={() => copy(newKey.value)}
          style={{ padding: '4px 12px', fontSize: 11, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer' }}>
          Copy key
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginTop: 10, marginBottom: 6, borderBottom: '1px solid oklch(0.78 0.10 145)' }}>
        {(['curl', 'mcp', 'env'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{
              padding: '4px 10px', fontSize: 11, fontFamily: 'var(--mono)',
              background: 'transparent', border: 'none',
              borderBottom: tab === t ? '2px solid oklch(0.32 0.06 145)' : '2px solid transparent',
              color: tab === t ? 'oklch(0.32 0.06 145)' : 'var(--ink-2)',
              cursor: 'pointer', marginBottom: -1,
            }}
          >
            {t === 'curl' ? 'curl (REST)' : t === 'mcp' ? 'Claude Desktop / Cursor (MCP)' : 'shell env'}
          </button>
        ))}
      </div>
      <pre style={{
        margin: 0, padding: 10,
        fontFamily: 'var(--mono)', fontSize: 11.5, lineHeight: 1.45,
        background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 4,
        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 220, overflow: 'auto',
      }}>{snippets[tab]}</pre>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={() => copy(snippets[tab])}
          style={{ padding: '4px 12px', fontSize: 11, background: 'var(--ink)', color: 'var(--bg)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          Copy {tab === 'mcp' ? 'config' : tab}
        </button>
        <button onClick={onDismiss}
          style={{ padding: '4px 12px', fontSize: 11, background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}>
          Got it
        </button>
        {tab === 'mcp' && (
          <span style={{ fontSize: 10.5, color: 'var(--ink-3)', alignSelf: 'center', marginLeft: 'auto' }}>
            Paste into <code style={{ fontFamily: 'var(--mono)' }}>~/Library/Application Support/Claude/claude_desktop_config.json</code>
          </span>
        )}
      </div>
    </div>
  );
}
